import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "February 10, 2026";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-black/5">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-home-link">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900 text-[15px]">Mission Watch</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-charcoal-900">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14" data-testid="terms-page">
        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Legal</p>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-charcoal-900 mt-2">Terms of Service</h1>
        <p className="text-sm text-charcoal-500 mt-3 font-mono">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-charcoal mt-10 max-w-none">
          <Section title="1. Agreement">
            <p>By creating an account or using Mission Watch (the &ldquo;Service&rdquo;), you agree to these Terms of Service. If you don&rsquo;t agree, please don&rsquo;t use the Service.</p>
          </Section>

          <Section title="2. What the Service does">
            <p>Mission Watch is an automated monitoring tool for the public digital footprint of small nonprofits. We fetch and analyze the public pages you point us to (like a search engine would), then send you monthly Mission Watch Score reports and instant alerts when things break. The Service is provided on an &ldquo;as-is,&rdquo; &ldquo;as-available&rdquo; basis.</p>
          </Section>

          <Section title="3. Your account">
            <ul>
              <li>You must be at least 16 years old and authorized to use the URLs you add.</li>
              <li>Keep your password confidential. You&rsquo;re responsible for activity under your account.</li>
              <li>Provide accurate contact and billing information and keep it current.</li>
            </ul>
          </Section>

          <Section title="4. Free trial and billing">
            <ul>
              <li>New subscribers get a 7-day free trial. A valid payment method is required to start the trial.</li>
              <li>Unless you cancel before the trial ends, we&rsquo;ll bill your card at the plan&rsquo;s stated monthly rate on the first day after the trial and every month thereafter.</li>
              <li>You can cancel any time from your dashboard or the Stripe billing portal. Cancellation takes effect at the end of the current billing period.</li>
              <li>Fees are non-refundable except where required by law.</li>
              <li>We may change prices with at least 30 days&rsquo; notice by email; the new price applies to your next billing cycle.</li>
            </ul>
          </Section>

          <Section title="5. Acceptable use">
            <p>You agree not to:</p>
            <ul>
              <li>Point the Service at URLs you don&rsquo;t own or don&rsquo;t have permission to monitor.</li>
              <li>Use the Service to attack, overload, or reverse-engineer other systems.</li>
              <li>Resell or white-label the Service without our written permission.</li>
              <li>Break the law or violate anyone&rsquo;s rights through the Service.</li>
            </ul>
          </Section>

          <Section title="6. AI-generated content">
            <p>Reports include AI-generated summaries and recommendations. These are provided as guidance, not professional advice. You are responsible for reviewing and validating AI output before acting on it. We are not liable for actions taken based on AI recommendations.</p>
          </Section>

          <Section title="7. Not professional advice">
            <p>Mission Watch is an operational monitoring tool. It is not legal, tax, accounting, cybersecurity-audit, or fundraising advice. Consult a qualified professional before making decisions in those areas.</p>
          </Section>

          <Section title="8. Availability and monitoring accuracy">
            <p>We strive for high availability, but we don&rsquo;t guarantee the Service will be uninterrupted or error-free. Automated checks can produce false positives or miss issues (e.g., because a website blocks crawlers, geo-restricts, or uses heavy client-side rendering). Always verify critical alerts before acting.</p>
          </Section>

          <Section title="9. Your content">
            <p>You retain ownership of the URLs and organization data you provide. You grant us a limited license to fetch, analyze, and display that data for the purpose of operating the Service.</p>
          </Section>

          <Section title="10. Intellectual property">
            <p>The Mission Watch name, logo, dashboard design, and software are our property. The Mission Watch Score, Verification Badge, and any custom reports we generate are licensed to you for your organization&rsquo;s use only.</p>
          </Section>

          <Section title="11. Termination">
            <p>You may cancel your subscription at any time. We may suspend or terminate accounts that violate these Terms, misuse the Service, or fail payment. On termination, your access ends and we may delete your data after 30 days.</p>
          </Section>

          <Section title="12. Warranty disclaimer">
            <p>THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo; TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
          </Section>

          <Section title="13. Limitation of liability">
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, MISSION WATCH&rsquo;S TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICE IS LIMITED TO THE AMOUNTS YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM. WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES.</p>
          </Section>

          <Section title="14. Changes to these Terms">
            <p>We may update these Terms occasionally. If we make material changes, we&rsquo;ll email account owners. Your continued use of the Service after changes means you accept the updated Terms.</p>
          </Section>

          <Section title="15. Governing law">
            <p>These Terms are governed by the laws of the state where Mission Watch is organized, without regard to its conflict-of-laws rules. Any disputes will be resolved in the state or federal courts located there.</p>
          </Section>

          <Section title="16. Contact">
            <p>Questions? Email <a href="mailto:hello@missionwatch.app" className="text-brand hover:underline">hello@missionwatch.app</a>.</p>
          </Section>
        </div>

        <div className="mt-14 pt-6 border-t border-black/5 flex items-center justify-between text-sm">
          <Link to="/" className="text-charcoal-500 hover:text-charcoal-900">← Back to home</Link>
          <Link to="/privacy" className="text-brand hover:underline" data-testid="terms-to-privacy">Read Privacy Policy →</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold text-charcoal-900 mb-3">{title}</h2>
      <div className="text-base text-charcoal-600 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}
